import React, { useState } from "react";
import { Button, TextField, Paper, Box, Container, Typography, Link } from "@mui/material";
import { ErrorMessage, Field, Form, Formik } from "formik"; // eslint-disable-line no-unused-vars -- ErrorMessage ileride kullanılacak
import * as Yup from "yup";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import logo from "../assets/valonix-logo-siyah.png";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { toast } from "react-toastify";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // eslint-disable-line no-unused-vars -- ileride başarı ekranı için

const NAVY = '#1a365d';
const NAVY_HOVER = '#2c5282';

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="#" underline="hover">
        VALONIX
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: NAVY,
      dark: NAVY_HOVER,
    },
    secondary: {
      main: NAVY_HOVER,
    },
  },
});

const ResetPassword = () => {
  const { t } = useTranslation();
  const { reset_code } = useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required(t("auth.password.required"))
      .min(8, t("auth.password.min"))
      .test("has-uppercase", t("auth.password.upper"), (value) => /[A-Z]/.test(value || ""))
      .test(
        "has-special-char",
        t("auth.password.special"),
        (value) => /[@#$%^&*()\-._+=<>?/]/.test(value || "")
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], t("auth.password.mismatch"))
      .required(t("auth.password.confirmRequired")),
  });

  const handleSubmit = async (values) => {
    if (!reset_code) {
      toast.error(t("auth.reset.noCode"));
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post(
        `${API_URL}/auth/reset-password/${reset_code}`,
        {
          new_password: values.password,
          confirm_new_password: values.confirmPassword,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      console.log("Response:", response.data);
      toast.success(t("auth.reset.success"));
      
      // 2 saniye sonra login sayfasına yönlendir
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error("Bir hata oluştu:", error);
      const detail =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        t("auth.reset.error");
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: NAVY,
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              padding: { xs: 3, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={logo}
              alt={t("auth.logoAlt")}
              sx={{
                width: '60%',
                maxWidth: '250px',
                height: 'auto',
                marginBottom: 2,
              }}
            />

            {/* Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: NAVY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 2,
              }}
            >
              <LockResetIcon sx={{ fontSize: 32, color: '#fff' }} />
            </Box>

            <Typography
              component="h1"
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: 1,
                textAlign: 'center',
              }}
            >
              {t("auth.reset.title")}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                marginBottom: 3,
                textAlign: 'center',
                px: 2,
              }}
            >
              {t("auth.reset.subtitle")}
            </Typography>

            {/* Form */}
            <Formik
              initialValues={{ password: "", confirmPassword: "" }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched }) => (
                <Form style={{ width: '100%' }}>
                  <Field
                    as={TextField}
                    label={t("auth.password.new")}
                    name="password"
                    type="password"
                    placeholder={t("auth.reset.placeholder")}
                    fullWidth
                    required
                    disabled={submitting}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <Field
                    as={TextField}
                    label={t("auth.reset.confirm")}
                    name="confirmPassword"
                    type="password"
                    placeholder={t("auth.reset.confirmPlaceholder")}
                    fullWidth
                    required
                    disabled={submitting}
                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Şifre Kuralları */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: 'rgba(15, 39, 68, 0.05)',
                      border: '1px solid rgba(15, 39, 68, 0.2)',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1, fontWeight: 600 }}>
                      {t("auth.password.rulesTitle")}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      ✓ {t("auth.password.rule8")}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      ✓ {t("auth.password.ruleUpper")}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      ✓ {t("auth.password.ruleSpecial")}
                    </Typography>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '16px',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 4px 14px 0 rgba(15, 39, 68, 0.35)',
                      backgroundColor: NAVY,
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(15, 39, 68, 0.45)',
                        backgroundColor: NAVY_HOVER,
                      },
                    }}
                  >
                    {submitting ? t("auth.reset.updating") : t("auth.reset.submit")}
                  </Button>
                </Form>
              )}
            </Formik>

            <Copyright sx={{ mt: 3 }} />
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ResetPassword;